import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import SearchBar from '../components/SearchBar';
import HotelFormModal from '../components/hotels/HotelFormModal';
import RatePlanFormModal from '../components/hotels/RatePlanFormModal';
import { usePermissions } from '../hooks/usePermissions';
import {
  createAdminHotel,
  createHotelRatePlan,
  deleteAdminHotel,
  deleteHotelRatePlan,
  fetchAdminHotels,
  fetchHotelRatePlans,
  getNormalizedHotelRef,
  getNormalizedRatePlanId,
  updateAdminHotel,
  updateHotelRatePlan,
} from '../api/HotelAdmin';
import {
  AdminHotel,
  AdminHotelSaveInput,
  HotelRatePlan,
  HotelRatePlanUpsertPayload,
} from '../types/HotelAdmin';
import '../css/PackagesPage.css';
import '../css/Pages.css';
import '../css/HotelsPage.css';
import '../css/modal.css';
import * as FiIcons from 'react-icons/fi';

const FiPlus = FiIcons.FiPlus as unknown as React.FC;
const FiEdit2 = FiIcons.FiEdit2 as unknown as React.FC;
const FiTrash2 = FiIcons.FiTrash2 as unknown as React.FC;
const FiList = FiIcons.FiList as unknown as React.FC;

const formatAmount = (currency: string | undefined, amount: number | undefined) => {
  if (typeof amount !== 'number' || !Number.isFinite(amount)) return '-';
  return `${currency || 'INR'} ${amount}`;
};

const renderPricingRanges = (ratePlan: HotelRatePlan) => {
  const ranges = Array.isArray(ratePlan.pricing_ranges) ? ratePlan.pricing_ranges : [];
  if (!ranges.length) {
    return <span>{formatAmount(ratePlan.currency, ratePlan.price)}</span>;
  }

  return (
    <table className="hotels-pricing-summary-table">
      <thead>
        <tr>
          <th>Season</th>
          <th>Single</th>
          <th>Double</th>
        </tr>
      </thead>
      <tbody>
        {ranges.map((range, index) => (
          <tr key={`${range.start_date || 'any'}-${range.end_date || 'any'}-${index}`}>
            <td>{[range.start_date || 'Any', range.end_date || 'Any'].join(' to ')}</td>
            <td>{formatAmount(range.currency, range.single_occupancy_price)}</td>
            <td>{formatAmount(range.currency, range.double_occupancy_price)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const HotelsPage: React.FC = () => {
  const permissions = usePermissions();
  const canManageHotels = permissions.canManageHotels;

  const [hotels, setHotels] = useState<AdminHotel[]>([]);
  const [loadingHotels, setLoadingHotels] = useState(false);
  const [hotelSearch, setHotelSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  const [selectedHotel, setSelectedHotel] = useState<AdminHotel | null>(null);
  const [selectedHotelRef, setSelectedHotelRef] = useState('');
  const selectedHotelRefRef = useRef('');

  const [ratePlans, setRatePlans] = useState<HotelRatePlan[]>([]);
  const [loadingRatePlans, setLoadingRatePlans] = useState(false);
  const [showRatePlansModal, setShowRatePlansModal] = useState(false);

  const [showHotelModal, setShowHotelModal] = useState(false);
  const [editingHotel, setEditingHotel] = useState<AdminHotel | null>(null);
  const [savingHotel, setSavingHotel] = useState(false);

  const [showRatePlanModal, setShowRatePlanModal] = useState(false);
  const [editingRatePlan, setEditingRatePlan] = useState<HotelRatePlan | null>(null);
  const [savingRatePlan, setSavingRatePlan] = useState(false);

  useEffect(() => {
    selectedHotelRefRef.current = selectedHotelRef;
  }, [selectedHotelRef]);

  const loadHotels = useCallback(async () => {
    setLoadingHotels(true);
    try {
      const response = await fetchAdminHotels();
      const nextHotels = Array.isArray(response.items) ? response.items : [];
      setHotels(nextHotels);

      const currentHotelRef = selectedHotelRefRef.current;
      if (!currentHotelRef) return;

      const latestSelectedHotel =
        nextHotels.find((hotel) => getNormalizedHotelRef(hotel) === currentHotelRef) || null;

      if (!latestSelectedHotel) {
        selectedHotelRefRef.current = '';
        setSelectedHotel(null);
        setSelectedHotelRef('');
        setRatePlans([]);
        return;
      }

      setSelectedHotel(latestSelectedHotel);
    } catch (error: any) {
      setHotels([]);
      toast.error(error?.message || 'Failed to load hotels.');
    } finally {
      setLoadingHotels(false);
    }
  }, []);

  const loadRatePlans = useCallback(async (hotelRef: string) => {
    if (!hotelRef) {
      setRatePlans([]);
      return;
    }

    setLoadingRatePlans(true);
    try {
      const response = await fetchHotelRatePlans(hotelRef);
      setRatePlans(Array.isArray(response.items) ? response.items : []);
    } catch (error: any) {
      setRatePlans([]);
      toast.error(error?.message || 'Failed to load rate plans.');
    } finally {
      setLoadingRatePlans(false);
    }
  }, []);

  useEffect(() => {
    loadHotels();
  }, [loadHotels]);

  const filteredHotels = useMemo(() => {
    const query = hotelSearch.trim().toLowerCase();
    return hotels.filter((hotel) => {
      const isActive = !!hotel.is_active;
      if (statusFilter === 'active' && !isActive) return false;
      if (statusFilter === 'inactive' && isActive) return false;
      if (!query) return true;

      const values = [
        hotel.name,
        hotel.city_name,
        hotel.city_code,
        hotel.star_rating,
        getNormalizedHotelRef(hotel),
      ]
        .map((value) => (value === null || value === undefined ? '' : String(value).toLowerCase()));

      return values.some((value) => value.includes(query));
    });
  }, [hotels, hotelSearch, statusFilter]);


  const openCreateHotelModal = () => {
    setEditingHotel(null);
    setShowHotelModal(true);
  };

  const openEditHotelModal = (hotel: AdminHotel) => {
    setEditingHotel(hotel);
    setShowHotelModal(true);
  };

  const openRatePlans = async (hotel: AdminHotel) => {
    const hotelRef = getNormalizedHotelRef(hotel);
    if (!hotelRef) {
      toast.error('Unable to load rate plans: missing hotel reference.');
      return;
    }

    selectedHotelRefRef.current = hotelRef;
    setSelectedHotel(hotel);
    setSelectedHotelRef(hotelRef);
    setShowRatePlansModal(true);
    await loadRatePlans(hotelRef);
  };

  const handleSaveHotel = async (payload: AdminHotelSaveInput) => {
    setSavingHotel(true);
    try {
      if (editingHotel) {
        const hotelRef = getNormalizedHotelRef(editingHotel);
        if (!hotelRef) {
          toast.error('Unable to update hotel: missing hotel reference.');
          return;
        }
        await updateAdminHotel(hotelRef, payload);
        toast.success('Hotel updated successfully.');
      } else {
        await createAdminHotel(payload);
        toast.success('Hotel created successfully.');
      }

      setShowHotelModal(false);
      setEditingHotel(null);
      await loadHotels();
      if (selectedHotelRefRef.current) {
        await loadRatePlans(selectedHotelRefRef.current);
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save hotel.');
    } finally {
      setSavingHotel(false);
    }
  };

  const handleDeleteHotel = async (hotel: AdminHotel) => {
    const hotelRef = getNormalizedHotelRef(hotel);
    if (!hotelRef) {
      toast.error('Unable to delete hotel: missing hotel reference.');
      return;
    }

    const hotelLabel = hotel.name || hotelRef;
    if (!window.confirm(`Delete hotel "${hotelLabel}"?`)) return;

    try {
      await deleteAdminHotel(hotelRef);
      toast.success('Hotel deleted successfully.');

      const wasSelected = selectedHotelRefRef.current === hotelRef;
      if (wasSelected) {
        selectedHotelRefRef.current = '';
        setSelectedHotel(null);
        setSelectedHotelRef('');
        setRatePlans([]);
      }

      await loadHotels();
      if (!wasSelected && selectedHotelRefRef.current) {
        await loadRatePlans(selectedHotelRefRef.current);
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to delete hotel.');
    }
  };

  const openCreateRatePlanModal = () => {
    setEditingRatePlan(null);
    setShowRatePlanModal(true);
  };

  const openEditRatePlanModal = (ratePlan: HotelRatePlan) => {
    setEditingRatePlan(ratePlan);
    setShowRatePlanModal(true);
  };

  const handleSaveRatePlan = async (payload: HotelRatePlanUpsertPayload) => {
    const hotelRef = selectedHotelRefRef.current;
    if (!hotelRef) {
      toast.error('Select a hotel first.');
      return;
    }

    setSavingRatePlan(true);
    try {
      if (editingRatePlan) {
        const ratePlanId = getNormalizedRatePlanId(editingRatePlan);
        if (!ratePlanId) {
          toast.error('Unable to update rate plan: missing rate plan id.');
          return;
        }
        await updateHotelRatePlan(ratePlanId, payload);
        toast.success('Rate plan updated successfully.');
      } else {
        await createHotelRatePlan(hotelRef, payload);
        toast.success('Rate plan created successfully.');
      }

      setShowRatePlanModal(false);
      setEditingRatePlan(null);
      await loadRatePlans(hotelRef);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save rate plan.');
    } finally {
      setSavingRatePlan(false);
    }
  };

  const handleDeleteRatePlan = async (ratePlan: HotelRatePlan) => {
    const ratePlanId = getNormalizedRatePlanId(ratePlan);
    if (!ratePlanId) {
      toast.error('Unable to delete rate plan: missing rate plan id.');
      return;
    }

    const label = ratePlan.plan_code || ratePlan.room_name || ratePlanId;
    if (!window.confirm(`Delete rate plan "${label}"?`)) return;

    try {
      await deleteHotelRatePlan(ratePlanId);
      toast.success('Rate plan deleted successfully.');
      if (selectedHotelRefRef.current) {
        await loadRatePlans(selectedHotelRefRef.current);
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to delete rate plan.');
    }
  };

  const selectedHotelName = selectedHotel?.name || selectedHotelRef;
  const closeRatePlansModal = () => setShowRatePlansModal(false);

  return (
    <Layout>
      <div className="hotels-page">
        <div className="packages-header">
          <h2>Hotels</h2>
          <div className="packages-actions">
            {canManageHotels && (
              <button className="btn" onClick={openCreateHotelModal} type="button">
                <FiPlus /> Add Hotel
              </button>
            )}
          </div>
        </div>

        <div className="packages-filters hotels-filters">
          <SearchBar
            searchTerm={hotelSearch}
            onSearchChange={setHotelSearch}
            placeholder="Search by hotel name, city, or star rating"
          />
          <select
            className="filter-select"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as 'all' | 'active' | 'inactive')}
            aria-label="Filter by hotel active status"
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div className="packages-table-wrapper">
          <table className="packages-table hotels-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>City</th>
                <th>Star</th>
                <th>Media</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingHotels && (
                <tr>
                  <td colSpan={7} className="empty-state">
                    Loading hotels...
                  </td>
                </tr>
              )}

              {!loadingHotels && filteredHotels.length === 0 && (
                <tr>
                  <td colSpan={7} className="empty-state">
                    No hotels found.
                  </td>
                </tr>
              )}

              {!loadingHotels &&
                filteredHotels.map((hotel) => {
                  const hotelRef = getNormalizedHotelRef(hotel);
                  const isSelected = hotelRef && hotelRef === selectedHotelRef;
                  return (
                    <tr key={hotelRef || `${hotel.providerHotelId || 'hotel'}-${hotel.name || ''}`} className={isSelected ? 'is-selected' : ''}>
                      <td>{hotel.name || '-'}</td>
                      <td>{[hotel.city_name, hotel.city_code].filter(Boolean).join(' / ') || '-'}</td>
                      <td>{hotel.star_rating ?? '-'}</td>
                      <td>
                        {(Array.isArray(hotel.images) ? hotel.images.length : 0)} images
                        {Array.isArray(hotel.videos) && hotel.videos.length > 0
                          ? ` / ${hotel.videos.length} videos`
                          : ''}
                      </td>
                      <td>
                        <span className={`badge ${hotel.is_active ? 'on' : 'off'}`}>
                          {hotel.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="col-actions">
                        <div className="action-buttons hotels-action-buttons">
                          <button className="btn-secondary" type="button" onClick={() => openRatePlans(hotel)}>
                            <FiList /> Rates
                          </button>
                          {canManageHotels && (
                            <>
                              <button className="btn-secondary" type="button" onClick={() => openEditHotelModal(hotel)}>
                                <FiEdit2 /> Edit
                              </button>
                              <button className="btn-secondary danger" type="button" onClick={() => handleDeleteHotel(hotel)}>
                                <FiTrash2 /> Delete
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        {showRatePlansModal && selectedHotel && (
          <div className="modal-backdrop modal-level-1" onClick={closeRatePlansModal}>
            <div
              className="modal modal-lg"
              onClick={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="rate-plans-title"
            >
              <div className="modal-header">
                <div>
                  <h3 id="rate-plans-title">Room Variants - {selectedHotelName}</h3>
                  <div className="rfq-muted">Manage the selected hotel&apos;s room variants.</div>
                </div>
                <div className="modal-header-actions">
                  {canManageHotels && (
                    <button className="btn" type="button" onClick={openCreateRatePlanModal}>
                      <FiPlus /> Add Room Variant
                    </button>
                  )}
                  <button
                    className="close-btn"
                    type="button"
                    aria-label="Close room variants"
                    onClick={closeRatePlansModal}
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="modal-body">
                <div className="packages-table-wrapper">
                  <table className="packages-table hotels-table">
                    <thead>
                      <tr>
                        <th>Room Type</th>
                        <th>Meal Type</th>
                        <th>Pricing</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingRatePlans && (
                        <tr>
                          <td colSpan={4} className="empty-state">
                            Loading room variants...
                          </td>
                        </tr>
                      )}

                      {!loadingRatePlans && ratePlans.length === 0 && (
                        <tr>
                          <td colSpan={4} className="empty-state">
                            No room variants found for this hotel.
                          </td>
                        </tr>
                      )}

                      {!loadingRatePlans &&
                        ratePlans.map((ratePlan) => {
                          const ratePlanId = getNormalizedRatePlanId(ratePlan);
                          return (
                            <tr key={ratePlanId || `${ratePlan.plan_code || 'plan'}-${ratePlan.room_name || ''}`}>
                              <td>{ratePlan.room_name || '-'}</td>
                              <td>{ratePlan.meal_type || '-'}</td>
                              <td className="hotels-pricing-summary-cell">{renderPricingRanges(ratePlan)}</td>
                              <td className="col-actions">
                                {canManageHotels ? (
                                  <div className="action-buttons hotels-action-buttons">
                                    <button
                                      className="btn-secondary"
                                      type="button"
                                      onClick={() => openEditRatePlanModal(ratePlan)}
                                    >
                                      <FiEdit2 /> Edit
                                    </button>
                                    <button
                                      className="btn-secondary danger"
                                      type="button"
                                      onClick={() => handleDeleteRatePlan(ratePlan)}
                                    >
                                      <FiTrash2 /> Delete
                                    </button>
                                  </div>
                                ) : (
                                  '-'
                                )}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <HotelFormModal
        isOpen={showHotelModal}
        initialHotel={editingHotel}
        submitting={savingHotel}
        onClose={() => {
          setShowHotelModal(false);
          setEditingHotel(null);
        }}
        onSubmit={handleSaveHotel}
      />

      <RatePlanFormModal
        isOpen={showRatePlanModal}
        initialRatePlan={editingRatePlan}
        submitting={savingRatePlan}
        onClose={() => {
          setShowRatePlanModal(false);
          setEditingRatePlan(null);
        }}
        onSubmit={handleSaveRatePlan}
      />
    </Layout>
  );
};

export default HotelsPage;
